import React, { useState, useEffect } from 'react';
import api from '../api';

const emptyForm = { item: '', quantity: '', unit_price: '', supplier: '', member: '' };

const Purchases = () => {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/purchases/'),
      api.get('/suppliers/').catch(() => ({ data: [] })),
      api.get('/users/').catch(() => ({ data: [] })),
    ])
      .then(([pRes, sRes, uRes]) => {
        setRows(Array.isArray(pRes.data) ? pRes.data : pRes.data.results || []);
        setSuppliers(Array.isArray(sRes.data) ? sRes.data : sRes.data.results || []);
        const users = Array.isArray(uRes.data) ? uRes.data : uRes.data.results || [];
        setMembers(users.filter(u => u.role === 'user'));
      })
      .catch(() => setError('Failed to load purchases'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const quantity = Number(form.quantity);
      const unit_price = Number(form.unit_price);
      const payload = {
        item: form.item,
        quantity,
        unit_price,
        supplier: Number(form.supplier),
        total: quantity * unit_price,
      };
      if (form.member) payload.member = Number(form.member);
      await api.post('/purchases/', payload);
      setShowModal(false);
      setForm(emptyForm);
      load();
    } catch {
      setError('Failed to create purchase');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-card"><p className="page-subtitle">Loading purchases…</p></div>;

  return (
    <div>
      <div className="flex-between">
        <p className="page-subtitle" style={{ margin: 0 }}>
          Record member buys — each create posts Dr Inventory / Cr Accounts Payable.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setShowModal(true)}>
          New Purchase
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {rows.length === 0 ? (
        <div className="empty-state">
          <strong>No purchases yet</strong>
          <p>Create the first recorded purchase to start the aggregation cycle.</p>
          <button type="button" className="btn btn-primary" onClick={() => setShowModal(true)}>
            New Purchase
          </button>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Supplier</th>
                <th>Item</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Total</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td>{r.member_name}</td>
                  <td>{r.supplier_name}</td>
                  <td>{r.item}</td>
                  <td>{r.quantity}</td>
                  <td>{Number(r.unit_price).toLocaleString()}</td>
                  <td>{Number(r.total).toLocaleString()}</td>
                  <td>{r.date}</td>
                  <td><span className={`badge ${r.status}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Create Purchase</h3>
            <p className="modal-lead">Pick a supplier from the list. Quantity × unit price becomes the line total.</p>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label htmlFor="p-item">Item</label>
                <input
                  id="p-item"
                  value={form.item}
                  onChange={e => setForm({ ...form, item: e.target.value })}
                  placeholder="e.g. LED Panels 24pc"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="p-qty">Quantity</label>
                  <input
                    id="p-qty"
                    type="number"
                    min="1"
                    value={form.quantity}
                    onChange={e => setForm({ ...form, quantity: e.target.value })}
                    placeholder="1"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="p-price">Unit Price</label>
                  <input
                    id="p-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.unit_price}
                    onChange={e => setForm({ ...form, unit_price: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="p-supplier">Supplier</label>
                <select
                  id="p-supplier"
                  value={form.supplier}
                  onChange={e => setForm({ ...form, supplier: e.target.value })}
                  required
                >
                  <option value="">Select supplier</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}{s.category ? ` · ${s.category}` : ''}</option>
                  ))}
                </select>
              </div>
              {members.length > 0 && (
                <div className="form-group">
                  <label htmlFor="p-member">Member (admins)</label>
                  <select
                    id="p-member"
                    value={form.member}
                    onChange={e => setForm({ ...form, member: e.target.value })}
                  >
                    <option value="">Self / default</option>
                    {members.map(m => (
                      <option key={m.id} value={m.id}>{m.username}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Purchases;
